// Workspace → Google imports (calendar:full). Every event pulled from the company
// Gmail's calendar, Added or Ignored; who sees it and as what type; Sync now;
// and the failure log. Title and time are Google's and can't be edited here —
// the database refuses it (20260918110000_google_pull.sql).
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Badge, Button, Select, type ToneName } from './primitives';
import { Card } from './card';
import { Dialog } from './dialog';
import { Field } from './field';
import { EmptyState, InlineStatus, LoadError, useToast, type StatusTone } from './feedback';
import { AUDIENCE_LABEL, AudiencePicker, whenText } from './calendar';
import { fmtRelative } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { useBelow } from '../lib/useMediaQuery';
import { departmentName, useDepartments } from '../lib/departments';
import { eventTypeLabel, pickableTypes, type Audience } from '../lib/calendarEvents';
import {
  listFailures,
  listImports,
  setImport,
  syncNow,
  type GoogleImport,
  type SyncFailure,
  type SyncResult,
} from '../lib/googleImports';

const muted: CSSProperties = {
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-3)',
  lineHeight: 'var(--hf-leading-body)',
};

type Loaded<T> = { data: T[]; error: { message: string } | null };

// What "Sync now" got back, in words. `tone` is how it went, not decoration:
// "not connected" is information, a failed sync is the only error.
const syncMessage = (r: SyncResult): { tone: StatusTone; text: string } => {
  if (!r.ok) return { tone: 'error', text: `Sync failed: ${r.error}` };
  if (!r.configured)
    return {
      tone: 'info',
      // No file path: the people reading this can't open the repo (WS-14).
      text: 'Google Calendar isn’t connected yet. An owner connects it once; ask them to finish the setup.',
    };
  return {
    tone: 'success',
    text: [
      r.pull &&
        `Synced: ${r.pull.added} added, ${r.pull.moved} moved, ${r.pull.cancelled} cancelled.`,
      r.push &&
        `${r.push.pushed} sent to Google` +
          (r.push.failed ? `, ${r.push.failed} not in Google yet.` : '.'),
    ]
      .filter(Boolean)
      .join(' '),
  };
};

export function ImportsTab() {
  const { tz, eventTypes } = useOrgDates();
  const departments = useDepartments();
  const toast = useToast();
  const [imports, setImports] = useState<Loaded<GoogleImport> | null>(null);
  const [failures, setFailures] = useState<Loaded<SyncFailure> | null>(null);
  const [syncing, setSyncing] = useState(false);
  // The last "Sync now" of this visit, and when: the only moment the app learns
  // whether Google is connected at all.
  const [synced, setSynced] = useState<{ result: SyncResult; at: number } | null>(null);
  const [editing, setEditing] = useState<GoogleImport | null>(null);
  // Ignore / Include errors, by import: shown on the row that failed, not at
  // the top of the card where nothing says which row it was (WS-14).
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const phone = useBelow('md');

  const load = useCallback(() => {
    void listImports().then(setImports);
    void listFailures().then(setFailures);
  }, []);
  useEffect(load, [load]);

  const sync = async () => {
    setSyncing(true);
    const result = await syncNow();
    setSyncing(false);
    setSynced({ result, at: Date.now() });
    load();
  };

  const save = async (imp: GoogleImport, change: Partial<GoogleImport>): Promise<string | null> => {
    const { error } = await setImport(imp.id, { ...imp, ...change });
    load();
    return error?.message ?? null;
  };
  const toggle = async (imp: GoogleImport) => {
    const error = await save(imp, { ignored: !imp.ignored });
    setRowErr((prev) => {
      const next = { ...prev };
      if (error) next[imp.id] = error;
      else delete next[imp.id];
      return next;
    });
    if (!error)
      toast.push({
        tone: 'success',
        message: imp.ignored
          ? `“${imp.title}” is back on the calendar.`
          : `“${imp.title}” is off the calendar.`,
      });
  };

  const audienceText = (imp: GoogleImport) =>
    imp.audience === 'department'
      ? imp.department_ids.map((id) => departmentName(departments, id) ?? 'Unknown').join(', ')
      : AUDIENCE_LABEL[imp.audience];

  // Connection, from what is known. Nothing records a successful sync, so until
  // "Sync now" is pressed the evidence is the log and the list: an open failure
  // is failing; anything imported or logged means Google has answered before.
  const failing = failures?.data.some((f) => !f.resolved_at);
  const r = synced?.result;
  const status: { tone: ToneName; label: string } =
    r && !r.ok
      ? { tone: 'danger', label: 'Sync failing' }
      : r && !r.configured
        ? { tone: 'neutral', label: 'Not connected' }
        : failing
          ? { tone: 'danger', label: 'Sync failing' }
          : r || imports?.data.length || failures?.data.length
            ? { tone: 'success', label: 'Connected' }
            : { tone: 'neutral', label: 'Not checked yet' };
  const message = r && syncMessage(r);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
      <Card
        label="Google Calendar"
        right={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hf-space-2)',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Badge tone={status.tone} dot>
              {status.label}
            </Badge>
            <Button
              size="sm"
              variant="primary"
              className="hf-hit"
              loading={syncing}
              onClick={() => void sync()}
            >
              Sync now
            </Button>
          </div>
        }
      >
        <div style={muted}>
          Meetings booked in the CRM land in the company Gmail’s calendar and appear here within 15
          minutes, as Admin-only Meetings. Change who sees one, or ignore it to take it off the
          calendar. Its title and time always come from Google.
        </div>
        {synced && r?.ok && r.configured && (
          <div style={{ ...muted, marginTop: 'var(--hf-space-2)' }}>
            Last synced {fmtRelative(synced.at)}.
          </div>
        )}
        {message && (
          <div style={{ marginTop: 'var(--hf-space-3)' }}>
            <InlineStatus tone={message.tone}>{message.text}</InlineStatus>
          </div>
        )}
      </Card>

      <Card
        label="Imported events"
        right={
          imports &&
          !imports.error && (
            // Counts in words, so the UI face: mono made "added" and "ignored"
            // read as code (critique 2026-10-06, mono carrying prose).
            <span style={{ ...muted, fontVariantNumeric: 'tabular-nums' }}>
              {imports.data.filter((i) => !i.ignored).length} added ·{' '}
              {imports.data.filter((i) => i.ignored).length} ignored
            </span>
          )
        }
      >
        {imports?.error ? (
          <LoadError what="the imported events" onRetry={load} />
        ) : (
          imports?.data.length === 0 && (
            <EmptyState title="Nothing imported yet">
              Meetings on the company Google Calendar appear here after the next sync.
            </EmptyState>
          )
        )}
        {!imports?.error && !!imports?.data.length && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {imports.data.map((imp, i) => {
              const status = (
                <Badge tone={imp.ignored ? 'neutral' : 'success'}>
                  {imp.ignored ? 'Ignored' : 'Added'}
                </Badge>
              );
              const actions = (
                <>
                  {!imp.ignored && (
                    <Button
                      size="sm"
                      className="hf-hit"
                      ariaLabel={`Change ${imp.title}`}
                      onClick={() => setEditing(imp)}
                    >
                      Change
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="hf-hit"
                    ariaLabel={`${imp.ignored ? 'Include' : 'Ignore'} ${imp.title}`}
                    onClick={() => void toggle(imp)}
                  >
                    {imp.ignored ? 'Include' : 'Ignore'}
                  </Button>
                </>
              );
              return (
                <li
                  key={imp.id}
                  style={{
                    display: 'flex',
                    // On a phone the row stacks: the event, then one line with
                    // its state at the left and what to do at the right, where
                    // the thumb is. Wrapped, the buttons fell to the left edge
                    // under a badge that stayed beside the title.
                    ...(phone
                      ? { flexDirection: 'column', alignItems: 'stretch' }
                      : { alignItems: 'center', flexWrap: 'wrap' }),
                    gap: 'var(--hf-space-3)',
                    padding: 'var(--hf-space-3) var(--hf-space-1)',
                    borderTop: i ? '1px solid var(--hf-rule)' : undefined,
                  }}
                >
                  <div style={{ flex: phone ? undefined : 1, minWidth: phone ? 0 : 220 }}>
                    {/* An ignored row dims its text only. Dimming the whole row
                        also dimmed "Include", the one thing to press on it. */}
                    <div
                      style={{
                        fontSize: 'var(--hf-text-md)',
                        color: imp.ignored ? 'var(--hf-ink-3)' : 'var(--hf-ink)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {imp.title}
                    </div>
                    <div style={{ ...muted, overflowWrap: 'anywhere' }}>
                      {whenText(imp, tz)}
                      {imp.location ? ` · ${imp.location}` : ''}
                    </div>
                    {!imp.ignored && (
                      <div style={muted}>
                        {eventTypeLabel(eventTypes, imp.type)} · {audienceText(imp)}
                      </div>
                    )}
                  </div>
                  {phone ? (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-3)' }}
                    >
                      {status}
                      <div style={{ flex: 1 }} />
                      {actions}
                    </div>
                  ) : (
                    <>
                      {status}
                      {actions}
                    </>
                  )}
                  {rowErr[imp.id] && (
                    // A line of its own: under the buttons, not beside them.
                    <div style={phone ? undefined : { flexBasis: '100%' }}>
                      <InlineStatus tone="error">
                        Couldn’t {imp.ignored ? 'include' : 'ignore'} this event. {rowErr[imp.id]}
                      </InlineStatus>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card label="Sync failures">
        {/* The one surface meant to reveal breakage must not say "no failures"
            when the log itself could not be read (WS-V1). */}
        {failures?.error ? (
          <LoadError what="the sync failure log" onRetry={load} />
        ) : (
          failures?.data.length === 0 && <div style={muted}>No sync failures.</div>
        )}
        {!failures?.error && !!failures?.data.length && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {failures.data.map((f, i) => (
              <li
                key={f.id}
                style={{
                  padding: 'var(--hf-space-2) 0',
                  borderTop: i ? '1px solid var(--hf-rule)' : undefined,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--hf-space-2)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Badge tone={f.resolved_at ? 'neutral' : 'danger'}>
                    {f.resolved_at ? 'Resolved' : 'Failing'}
                  </Badge>
                  <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
                    {f.kind === 'pull'
                      ? 'Reading Google'
                      : f.event
                        ? `Sending “${f.event.title}” to Google`
                        : 'Updating the Hyprflow calendar in Google'}{' '}
                    · {f.attempts} {f.attempts === 1 ? 'attempt' : 'attempts'} · since{' '}
                    {fmtRelative(f.first_failed_at)}
                    {f.resolved_at ? ` · closed ${fmtRelative(f.resolved_at)}` : ''}
                  </span>
                </div>
                <div style={{ ...muted, marginTop: 'var(--hf-space-1)', overflowWrap: 'anywhere' }}>
                  {f.error}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editing && (
        <ChangeImport
          imp={editing}
          onClose={() => setEditing(null)}
          onSave={async (change) => {
            const error = await save(editing, change);
            if (!error) {
              setEditing(null);
              toast.push({ tone: 'success', message: `Saved who sees “${editing.title}”.` });
            }
            return error;
          }}
        />
      )}
    </div>
  );
}

function ChangeImport({
  imp,
  onClose,
  onSave,
}: {
  imp: GoogleImport;
  onClose: () => void;
  /** Resolves to the error, or null once saved. */
  onSave: (
    change: Pick<GoogleImport, 'type' | 'audience' | 'department_ids'>,
  ) => Promise<string | null>;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { eventTypes } = useOrgDates();
  const departments = useDepartments();
  const [type, setType] = useState(imp.type);
  const [audience, setAudience] = useState<Audience>(imp.audience);
  const [ids, setIds] = useState<string[]>(imp.department_ids);
  const noDepartment = audience === 'department' && ids.length === 0;
  const dirty =
    type !== imp.type || audience !== imp.audience || ids.join() !== imp.department_ids.join();
  // Type and the audience radios are buttons, and a dialog opens on its first
  // input: that was a department's checkbox, past both, or the Close button.
  const first = useRef<HTMLDivElement>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    const error = await onSave({ type, audience, department_ids: ids });
    // Saved: the parent has closed this dialog, so there is nothing left to set.
    if (error) {
      setErr(error);
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={imp.title}
      size="sm"
      onClose={onClose}
      dirty={dirty}
      initialFocus={first}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" size="sm" onClick={requestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={noDepartment}
            loading={busy}
            onClick={() => void submit()}
          >
            Save
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        <div ref={first}>
          <Field label="Type">
            {(control) => (
              <Select
                {...control}
                value={type}
                onChange={setType}
                options={pickableTypes(eventTypes, imp.type).map((t) => ({
                  value: t.id,
                  label: t.archived ? `${t.label} (archived)` : t.label,
                }))}
              />
            )}
          </Field>
        </div>
        <AudiencePicker
          audience={audience}
          departmentIds={ids}
          departments={departments.filter((d) => !d.archived || imp.department_ids.includes(d.id))}
          // Why Save is off, said where the fix is (UI-CONVENTIONS §6).
          error={noDepartment ? 'Pick at least one department.' : undefined}
          onChange={(a, d) => {
            setAudience(a);
            setIds(d);
          }}
        />
        <div style={muted}>People this brings into the audience are told it was posted.</div>
        {/* In the dialog, not a toast: the page behind a modal is inert. */}
        {err && <InlineStatus tone="error">{err}</InlineStatus>}
      </div>
    </Dialog>
  );
}
